// Honey points, levels and the ladder, copied from the iOS Models.swift.
import { isComplete, stepsDone, type Project } from './plan';

export const SCORE = { perStep: 10, perEquipment: 5, completionBonus: 25 } as const;

export function points(p: Project): number {
  return stepsDone(p) * SCORE.perStep
    + p.equipment.filter((e) => e.done).length * SCORE.perEquipment
    + (isComplete(p) ? SCORE.completionBonus : 0);
}

export const totalPoints = (projects: Project[]): number =>
  projects.reduce((sum, p) => sum + (p.deletedAt ? 0 : points(p)), 0);

export interface Level { name: string; threshold: number }

/**
 * Ascending, no ties. The first five keep their iOS thresholds, so a Hive
 * Hero yesterday is a Hive Hero today; past those, the gaps widen and the
 * names are a joke about persistence.
 */
export const LEVELS: readonly Level[] = [
  ['New Bee', 0], ['Busy Bee', 50], ['Handy Honey', 150], ['Hive Hero', 300], ['Queen Bee', 500],
  ['Grout Grasshopper', 650], ['Waggle Dancer', 800], ['Caulk of the Town', 1_000], ['Duct Tape Devotee', 1_200],
  ['Shim Sherpa', 1_400], ['Pollen Pro', 1_650], ['Plumb Bob Prodigy', 1_900], ['Stud Finder General', 2_200],
  ['Sawdust Sommelier', 2_500], ['Wax Poetic', 2_850], ['Allen Key Adept', 3_200], ['Primer Minister', 3_600],
  ['Buzzsaw Virtuoso', 4_000], ['Nectar Contractor', 4_500], ['Drill Sergeant', 5_000], ['Mitre Maestro', 5_600],
  ['Hive Inspector', 6_200], ['Bee-spoke Joiner', 6_900], ['Putty Paragon', 7_600], ['Swarm Boss', 8_400],
  ['Rafter Regent', 9_200], ['Honeycomb Architect', 10_000], ['Grand Poobee', 11_000], ['Hinge Benefit', 12_000],
  ['Tile Tycoon', 13_000], ['Apiary Artisan', 14_500], ['Dowel Dignitary', 16_000], ['Sandpaper Sage', 17_500],
  ['Chancellor of Caulk', 19_000], ['Wrench Whisperer', 21_000], ['Justice of the Joists', 23_000],
  ['Beekeeper Emeritus', 25_000], ['Hardware Store Regular', 27_500], ['Aisle Seven Legend', 30_000],
  ['Marshal of the Mudroom', 33_000], ['Patron Saint of Patching', 36_000], ['Combmaster General', 40_000],
  ['Toolbelt Immortal', 44_000], ['Certified Buzzworthy', 48_000], ['Pollen Potentate', 53_000],
  ['Sovereign of Sealant', 58_000], ['Oracle of the Off-Cut', 64_000], ['Nectar Magnate', 70_000],
  ['Warden of the Wax', 77_000], ['Colossus of Comb', 85_000], ['Unbeelievable', 93_000], ['Apex Apiarist', 102_000],
  ['Hive Mind', 112_000], ['Bee All, End All', 125_000], ['Nectar of the Gods', 150_000],
].map(([name, threshold]) => ({ name: name as string, threshold: threshold as number }));

export function levelAt(pts: number): Level {
  let current = LEVELS[0]!;
  for (const l of LEVELS) if (l.threshold <= pts) current = l;
  return current;
}

export const nextLevel = (pts: number): Level | undefined => LEVELS.find((l) => l.threshold > pts);

/** Where the meter sits between this level and the next: 0..1, and 1 at the top rung. */
export function levelProgress(pts: number): { level: Level; next?: Level; fraction: number; toNext: number } {
  const level = levelAt(pts), next = nextLevel(pts);
  if (!next) return { level, fraction: 1, toNext: 0 };
  const span = next.threshold - level.threshold;
  return { level, next, fraction: (pts - level.threshold) / span, toNext: next.threshold - pts };
}
